import { SKYAGENT_URL } from "@/lib/auth";
import { useSkyAgentNav, type SkyAgentNavItem } from "@/hooks/useSkyAgentNav";

/** The current Performance Dashboard's href within SkyAgent's nav. */
const PERFORMANCE_DASHBOARD_HREF = "/operations-dashboard";

/** SkyAgent nav groups → the section labels used in the SkyAgent sidebar. */
const GROUP_LABELS: Record<SkyAgentNavItem["group"], string> = {
  ops: "Operations",
  driver: "Agent Tools",
  sales: "Sales",
};
const GROUP_ORDER: SkyAgentNavItem["group"][] = ["ops", "driver", "sales"];

/**
 * The SkyAgent left-hand menu, rendered inside the Performance Dashboard so
 * users can move between SkyAgent sections without losing the navigation
 * (NAT-55). Items link back to SkyAgent (a different origin), so these are
 * plain full-navigation anchors; the shared SSO session means no re-login.
 *
 * Renders nothing until the nav resolves (or when not in skyagent mode), so the
 * dashboard simply shows full-width rather than reserving empty chrome.
 */
export function SkyAgentNav() {
  const { data: items } = useSkyAgentNav();

  if (!items || items.length === 0) return null;

  const base = SKYAGENT_URL.replace(/\/$/, "");

  return (
    <nav
      aria-label="SkyAgent navigation"
      className="w-56 shrink-0 border-r border-border-subtle bg-surface-card/70"
    >
      <div className="sticky top-0 flex flex-col gap-4 p-4">
        {GROUP_ORDER.map((group) => {
          const groupItems = items.filter((item) => item.group === group);
          if (groupItems.length === 0) return null;
          return (
            <div key={group} className="flex flex-col gap-1">
              <p className="px-2 text-xs font-medium uppercase tracking-wide text-fg-muted">
                {GROUP_LABELS[group]}
              </p>
              {groupItems.map((item) => {
                const isCurrent = item.href === PERFORMANCE_DASHBOARD_HREF;
                return (
                  <a
                    key={item.href}
                    href={`${base}${item.href}`}
                    aria-current={isCurrent ? "page" : undefined}
                    className={
                      isCurrent
                        ? "rounded-lg bg-info/15 px-2 py-2 text-sm font-semibold text-info-text"
                        : "rounded-lg px-2 py-2 text-sm font-medium text-fg-secondary hover:bg-surface-raised/60 hover:text-fg-primary"
                    }
                  >
                    {item.label}
                  </a>
                );
              })}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
